import React from 'react'
import { useParams, useLocation } from 'react-router-dom'

import { QuizIcon } from '../../components/QuizIcon/QuizIcon'

import Header from './components/Header/Header'
import {
  Wrap,
  HalfWrap,
  Hr,
  QuizEntry,
  QuizEntryInner,
  FlexWrap,
  QuizTitle,
} from './styled'
import { OtherUserPreview } from './components/OtherUserPreview/OtherUserPreview'
import { OneQuizComparison } from './components/OneQuizComparison/OneQuizComparison'
import { QuizMatchValue } from './components/QuizMatchValue/QuizMatchValue'

import { EmptyState } from 'src/components/EmptyState/EmptyState'
import { getConnection } from 'src/api/connections/connections'
import { Connection } from 'src/api/connections/types'
import { LoadingScreen } from 'src/components/Loading'
import { getQuizAnswers } from 'src/api/quizAnswers/quizAnswers'
import { QuizAnswers } from 'src/api/quizAnswers/types'
import { useMedia } from 'src/hooks/useMedia'
import { breakpoints } from 'src/styles/media'

export const LeaderBoardDetail: React.FC = () => {
  const { id } = useParams()
  const { search } = useLocation()
  const isDesktop = useMedia(breakpoints.desktop)

  const [connection, setConnection] = React.useState<Connection>()
  const [isLoading, setIsLoading] = React.useState(true)
  const [selectedQuizId, setSelectedQuizId] = React.useState<number>()
  const [selectedQuizAnswers, setSelectedQuizAnswers] = React.useState<
    QuizAnswers
  >()

  const preselectedQuizId = new URLSearchParams(search).get('quizId')

  const [oneQuizComparisonOpen, setOneQuizComparisonOpen] = React.useState(
    preselectedQuizId ? true : false
  )

  React.useEffect(() => {
    const fetchConnection = async () => {
      try {
        const data = await getConnection(id)
        setConnection(data)

        if (isDesktop || preselectedQuizId) {
          const initialQuizId = preselectedQuizId
            ? Number(preselectedQuizId)
            : data.otherUser.completedQuizzes[0].id

          const quizAnswers = await getQuizAnswers(
            initialQuizId,
            data.otherUser.id
          )
          setSelectedQuizAnswers(quizAnswers)
          setSelectedQuizId(initialQuizId)
        }
      } catch (e) {
        // notify user something went wrong
        console.error(e)
      }
      setIsLoading(false)
    }
    fetchConnection()
  }, [id, isDesktop, preselectedQuizId])

  const handleQuizClick = async (quizId: number) => {
    if (connection) {
      const quizAnswers = await getQuizAnswers(quizId, connection.otherUser.id)
      setSelectedQuizAnswers(quizAnswers)
      setOneQuizComparisonOpen(true)
    }
    setSelectedQuizId(quizId)
  }

  const selectedQuiz = React.useMemo(
    () =>
      connection?.otherUser.completedQuizzes.find(
        (quiz) => quiz.id === selectedQuizId
      ),
    [connection, selectedQuizId]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  return connection ? (
    <>
      <Header
        closeFirstImpressions={() => setOneQuizComparisonOpen(false)}
        firstImpressionsOpen={oneQuizComparisonOpen}
        otherUser={connection.otherUser}
      />
      <Wrap>
        <HalfWrap isHidden={!isDesktop && oneQuizComparisonOpen}>
          <OtherUserPreview connection={connection} />
          <Hr />
          {connection.otherUser.completedQuizzes.length > 0 ? (
            connection.otherUser.completedQuizzes.map(
              (otherUserCompleteQuiz) => (
                <QuizEntry
                  key={otherUserCompleteQuiz.id}
                  isActive={selectedQuizId === otherUserCompleteQuiz.id}
                  onClick={async () =>
                    await handleQuizClick(otherUserCompleteQuiz.id)
                  }
                  disabled={otherUserCompleteQuiz.matchPercent === null}
                >
                  <QuizEntryInner>
                    <FlexWrap>
                      <QuizIcon
                        color={otherUserCompleteQuiz.colors.bgStart}
                        imgSrc={otherUserCompleteQuiz.imageUrl}
                      />
                      <QuizTitle>{otherUserCompleteQuiz.title}</QuizTitle>
                    </FlexWrap>
                    <QuizMatchValue
                      quizId={otherUserCompleteQuiz.id}
                      matchPercent={otherUserCompleteQuiz.matchPercent}
                    />
                  </QuizEntryInner>
                </QuizEntry>
              )
            )
          ) : (
            <EmptyState
              title="No quizzes"
              body="This user hasn't taken any quiz yet."
            />
          )}
        </HalfWrap>
        <HalfWrap withGradient isHidden={!oneQuizComparisonOpen}>
          {selectedQuizAnswers ? (
            <OneQuizComparison
              quizAnswers={selectedQuizAnswers}
              otherUserProfilePhoto={connection.otherUser.profilePicture}
              matchPercent={connection.matchPercent}
              selectedQuiz={selectedQuiz}
            />
          ) : null}
        </HalfWrap>
      </Wrap>
    </>
  ) : null
}
